/**
 * The shared Ask-AI conversation engine — the one-shot POST + warming auto-retry
 * loop, the optimistic exchange overlay, the session-cache write and the
 * error-kind classification, extracted so both the full Ask page (`routes/_auth/
 * ask.tsx`) and the ask-anywhere overlay (`components/AskOverlay`) drive questions
 * through the exact same flow rather than each hand-rolling the mutation.
 *
 * What differs between the two surfaces is only *how the active session is
 * tracked* — the page keeps it in the URL (`?session=`), the overlay in local
 * state — so the caller supplies `activeSessionId` plus a small set of callbacks
 * (session created / gone, composer draft accepted / restore) and this hook owns
 * everything else. The scope is passed to {@link IUseAskFlow.runAsk} already
 * reduced to the wire body, so the hook is agnostic to each surface's scope UI.
 */

import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import { AskOutOfCreditsError, AskSessionNotFoundError, AskUnavailableError, askQuestion } from "../lib/api"
import { queryKeys } from "../lib/query-keys"
import {
  type IAskCitation,
  type IAskDisplayMessage,
  type IAskMessage,
  type IAskScope,
  type IAskSessionDetail,
  isAskRefusal,
  type TAskErrorKind,
} from "../lib/types/ask"
import { useAuth } from "./useAuth"
import { useCompanyApi } from "./useCompanyApi"
import { useCompanyMutation } from "./useCompanyQuery"

/** How many times to re-POST while the engine reports `warming` before giving up
 * with a soft "still preparing" banner. */
const MAX_WARMING_ATTEMPTS = 10

/** Backoff for the warming retry loop — grows, capped so we never stall long. */
function warmingBackoffMs(attempt: number): number {
  return Math.min(700 + (attempt - 1) * 350, 3000)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** The in-flight (or just-resolved) exchange rendered as an optimistic overlay on
 * top of the persisted thread. */
export interface IPendingExchange {
  question: string
  status: "thinking" | "warming" | "done"
  /** Resolved on success — keeps a brand-new chat's answer on screen until the
   * caller's active-session id catches up to the freshly-created session id. */
  sessionId?: string
  answer?: string
  citations?: IAskCitation[]
  followUps?: string[]
}

export interface IUseAskFlowParams {
  /** The session the next question threads into (the page's URL param, the
   * overlay's local state). `null` starts a fresh chat. */
  activeSessionId: string | null
  /** A brand-new chat materialised a session id — the page deep-links the URL,
   * the overlay stores it in local state. Not called when continuing a session. */
  onSessionCreated: (sessionId: string) => void
  /** The threaded session vanished server-side (404 mid-conversation) — drop it
   * (the page clears `?session=`, the overlay clears its local id). */
  onSessionGone?: () => void
  /** A question was accepted and sent — clear the composer draft. */
  onQuestionAccepted?: () => void
  /** A soft failure (warming timeout / error) — restore the question into the
   * composer so a one-click retry is possible. */
  onRestoreQuestion?: (question: string) => void
}

export interface IUseAskFlow {
  /** The in-flight / just-resolved optimistic exchange (`null` when idle). */
  pending: IPendingExchange | null
  /** Which failure banner to show (`null` when clear). */
  errorKind: TAskErrorKind | null
  setErrorKind: (kind: TAskErrorKind | null) => void
  /** True while a question is in flight — disables the composer. */
  busy: boolean
  /**
   * Ask a question — the single funnel for the composer, follow-up chips and
   * example cards. `scope` is the already-reduced wire body (`undefined` = all
   * meetings); each surface converts its own scope UI before calling.
   */
  runAsk: (raw: string, scope: IAskScope | undefined) => void
  /** Drop the optimistic overlay (on new-chat / session switch / delete). */
  clearPending: () => void
  /**
   * Flatten a session's persisted turns plus the in-flight exchange into the
   * view's display shape. Pass the active session's messages (or `[]`).
   */
  buildThread: (persisted: IAskMessage[]) => IAskDisplayMessage[]
}

export function useAskFlow({
  activeSessionId,
  onSessionCreated,
  onSessionGone,
  onQuestionAccepted,
  onRestoreQuestion,
}: IUseAskFlowParams): IUseAskFlow {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()

  const [pending, setPending] = useState<IPendingExchange | null>(null)
  const [errorKind, setErrorKind] = useState<TAskErrorKind | null>(null)

  // Once the active session catches up to a brand-new chat's created id (and the
  // seeded cache is in place), retire the optimistic overlay.
  useEffect(() => {
    if (pending?.status === "done" && activeSessionId === pending.sessionId) {
      setPending(null)
    }
  }, [pending, activeSessionId])

  const askMutation = useCompanyMutation({
    mutationFn: async (
      client,
      { question, sessionId, scope }: { question: string; sessionId?: string; scope: IAskScope | undefined },
    ) => {
      let attempt = 0
      while (true) {
        const payload = await askQuestion(client, { question, sessionId, scope })
        if (payload.status === "ok") return payload
        // Engine cold — surface the warming state and back off before retrying.
        setPending((prev) => (prev ? { ...prev, status: "warming" } : prev))
        attempt += 1
        if (attempt >= MAX_WARMING_ATTEMPTS) return payload
        await sleep(warmingBackoffMs(attempt))
      }
    },
    onSuccess: (payload, variables) => {
      // Still warming after the retry budget — soft banner, restore the question.
      if (payload.status !== "ok" || !payload.sessionId) {
        setPending(null)
        setErrorKind("warming")
        onRestoreQuestion?.(variables.question)
        return
      }

      const sid = payload.sessionId
      const now = new Date().toISOString()
      const userMsg: IAskMessage = {
        id: `local-u-${now}-${sid}`,
        role: "user",
        content: variables.question,
        citations: null,
        model: null,
        createdAt: now,
      }
      const assistantMsg: IAskMessage = {
        id: `local-a-${now}-${sid}`,
        role: "assistant",
        content: payload.answer ?? "",
        citations: payload.citations ?? null,
        // The ask response carries no model; the background refetch below fills
        // it (and the real message ids + title) in from the persisted rows.
        model: null,
        createdAt: now,
        followUps: payload.followUps ?? null,
      }

      // Append the two new turns to the session-detail cache — a flash-free
      // optimistic write, no thread refetch for the immediate render.
      queryClient.setQueryData<IAskSessionDetail>(queryKeys.askSession(orgId, sid), (old) =>
        old
          ? { ...old, messages: [...old.messages, userMsg, assistantMsg] }
          : { id: sid, title: null, messages: [userMsg, assistantMsg] },
      )

      setPending((prev) =>
        prev
          ? {
              ...prev,
              status: "done",
              sessionId: sid,
              answer: payload.answer ?? "",
              citations: payload.citations,
              followUps: payload.followUps,
            }
          : prev,
      )

      // Reconcile with the server truth in the background (real ids + model +
      // title) — react-query swaps seamlessly since the turns already match.
      void queryClient.invalidateQueries({ queryKey: queryKeys.askSession(orgId, sid) })
      // Refresh the sidebar (new session appears / title + updatedAt bump).
      void queryClient.invalidateQueries({ queryKey: queryKeys.askSessions(orgId) })

      // A brand-new chat — hand the created id back to the caller (URL / state).
      if (activeSessionId !== sid) onSessionCreated(sid)
    },
    onError: (error, variables) => {
      setPending(null)
      // Restore the question so a one-click retry (or edit) is possible.
      onRestoreQuestion?.(variables.question)
      if (error instanceof AskOutOfCreditsError) {
        setErrorKind("out-of-credits")
      } else if (error instanceof AskUnavailableError) {
        setErrorKind("unavailable")
      } else if (error instanceof AskSessionNotFoundError) {
        setErrorKind("generic")
        onSessionGone?.()
        void queryClient.invalidateQueries({ queryKey: queryKeys.askSessions(orgId) })
      } else {
        setErrorKind("generic")
      }
    },
  })

  // The one send path — the composer, a follow-up chip and an example card all
  // funnel through here so warming / optimistic / scope handling stays in one place.
  const runAsk = useCallback(
    (raw: string, scope: IAskScope | undefined): void => {
      const question = raw.trim()
      if (question.length === 0 || askMutation.isPending) return
      // No active workspace (e.g. an account that isn't in an org yet) must not
      // be a silent dead click — say why nothing will happen.
      if (!company) {
        setErrorKind("no-workspace")
        return
      }
      setErrorKind(null)
      setPending({ question, status: "thinking" })
      onQuestionAccepted?.()
      askMutation.mutate({ question, sessionId: activeSessionId ?? undefined, scope })
    },
    [company, activeSessionId, askMutation, onQuestionAccepted],
  )

  const clearPending = useCallback((): void => {
    setPending(null)
  }, [])

  const buildThread = useCallback(
    (persisted: IAskMessage[]): IAskDisplayMessage[] => {
      const messages: IAskDisplayMessage[] = persisted.map((message) => ({
        key: message.id,
        role: message.role,
        content: message.content,
        citations: message.citations,
        followUps: message.followUps,
        // A refusal is an assistant turn whose text is the exact canned refusal —
        // rendered plainly (no citations, distinct look).
        refused: message.role === "assistant" && isAskRefusal(message.content),
      }))

      // Show the optimistic overlay while in flight, and — for a brand-new chat —
      // until the active session reflects the created id (guards a one-frame empty
      // flash without duplicating the cache-appended turns for an existing session).
      const showOverlay = pending !== null && (pending.status !== "done" || activeSessionId !== pending.sessionId)
      if (showOverlay && pending) {
        messages.push({ key: "pending-user", role: "user", content: pending.question })
        messages.push({
          key: "pending-assistant",
          role: "assistant",
          content: pending.answer ?? "",
          citations: pending.citations,
          followUps: pending.followUps,
          refused: pending.answer ? isAskRefusal(pending.answer) : undefined,
          phase: pending.status === "done" ? "complete" : pending.status,
        })
      }

      return messages
    },
    [pending, activeSessionId],
  )

  return { pending, errorKind, setErrorKind, busy: askMutation.isPending, runAsk, clearPending, buildThread }
}
